import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    getUserReviewedFields,
    TransactionFieldReviewDto,
} from "@common/schemas/transactionFieldReview.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import {
    getEffectiveAccrualLedgerAccountId,
    getEffectiveGeneralLedgerAccountId,
} from "@common/helpers/transactions";
import {
    isCategoryTaxonomyReviewEligible,
    isLedgerAccountReviewEligible,
} from "@common/helpers/transactionFieldReviewEligibility";
import { UncategorizedAccountPredicateInput } from "@common/helpers/accounting/uncategorized-account";

export const isTransactionFieldReviewedByUser = (
    fieldReviews: TransactionFieldReviewDto[] | undefined,
    field: TransactionReviewField,
    userId: number | undefined,
) =>
    !!fieldReviews?.some(
        (review) => review.field === field && review.userId === userId,
    );

export const mergeTransactionFieldReviews = (
    existingReviews: TransactionFieldReviewDto[] | undefined,
    addedReviews: TransactionFieldReviewDto[],
) => [
    ...(existingReviews ?? []).filter(
        (existingReview) =>
            !addedReviews.some(
                (addedReview) =>
                    addedReview.field === existingReview.field &&
                    addedReview.userId === existingReview.userId,
            ),
    ),
    ...addedReviews,
];

export const removeTransactionFieldReview = (
    fieldReviews: TransactionFieldReviewDto[] | undefined,
    field: TransactionReviewField,
    userId: number | undefined,
) =>
    (fieldReviews ?? []).filter(
        (review) => !(review.userId === userId && review.field === field),
    );

export interface LedgerAccountsForReviewMarkable {
    cashAccount: UncategorizedAccountPredicateInput | null;
    accrualAccount: UncategorizedAccountPredicateInput | null;
}

export const isTransactionReviewFieldMarkable = (
    transaction: TransactionListItem,
    field: TransactionReviewField,
    ledgerAccounts: LedgerAccountsForReviewMarkable,
): boolean => {
    switch (field) {
        case TransactionReviewField.CATEGORY:
            return isCategoryTaxonomyReviewEligible(
                transaction.category?.taxonomy,
            );
        case TransactionReviewField.NOTE: {
            const memo = transaction.memo?.trim();
            return memo != null && memo.length > 0;
        }
        case TransactionReviewField.ENTITY:
            return transaction.entity?.id != null;
        // A blank ledger (no account resolved) or an account resolving to one
        // of the "Uncategorized Income/Expenses" placeholders is never
        // eligible for review — mirrors the backend's
        // `isLedgerAccountReviewEligible` check (transaction-field-review.
        // service.ts) and LedgerCell's own auto-review guard, so the row's
        // Confirm button never counts or offers a review it can't deliver.
        case TransactionReviewField.CASH_LEDGER:
            return (
                transaction.isBusiness &&
                isLedgerAccountReviewEligible(ledgerAccounts.cashAccount)
            );
        case TransactionReviewField.ACCRUAL_LEDGER:
            return (
                transaction.isBusiness &&
                isLedgerAccountReviewEligible(ledgerAccounts.accrualAccount)
            );
        default:
            return true;
    }
};

interface AccountForReviewMarkable {
    role?: UncategorizedAccountPredicateInput["role"];
    kickOriginalCode: number | null;
}

const toLedgerAccountPredicate = (
    account: AccountForReviewMarkable | undefined,
): UncategorizedAccountPredicateInput | null => {
    if (account == null) {
        return null;
    }
    return {
        role: account.role ?? null,
        kickOriginalCode: account.kickOriginalCode ?? null,
    };
};

export const getLedgerAccountsForReviewMarkable = (
    transaction: TransactionListItem,
    accountsById: Record<string, AccountForReviewMarkable | undefined>,
): LedgerAccountsForReviewMarkable => {
    const entityId = transaction.entity.id;
    const cashAccountId = getEffectiveGeneralLedgerAccountId({
        accountId: transaction.accountId,
        entityId,
        amount: transaction.amount,
        journalEntries: transaction.journalEntries,
    });
    const accrualAccountId = getEffectiveAccrualLedgerAccountId({
        accrualAccountId: transaction.accrualAccountId ?? null,
        entityId,
        amount: transaction.amount,
        journalEntries: transaction.journalEntries,
    });

    return {
        cashAccount: toLedgerAccountPredicate(
            cashAccountId ? accountsById[cashAccountId] : undefined,
        ),
        accrualAccount: toLedgerAccountPredicate(
            accrualAccountId ? accountsById[accrualAccountId] : undefined,
        ),
    };
};

export const getUnreviewedMarkableReviewFields = ({
    transaction,
    reviewFields,
    userId,
    ledgerAccounts,
}: {
    transaction: TransactionListItem;
    reviewFields: TransactionReviewField[];
    userId: number;
    ledgerAccounts: LedgerAccountsForReviewMarkable;
}): TransactionReviewField[] => {
    const reviewedFieldSet = getUserReviewedFields(
        transaction.fieldReviews,
        userId,
    );
    return reviewFields.filter(
        (field) =>
            isTransactionReviewFieldMarkable(
                transaction,
                field,
                ledgerAccounts,
            ) && !reviewedFieldSet.has(field),
    );
};
